import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, FileText, Image, ExternalLink, Clock, Loader2, Eye, X
} from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import ClarificationForm from './ClarificationForm';
import { formatStatus } from '../utils/statusUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UserRegistrationDetails = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [registration, setRegistration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clarifications, setClarifications] = useState([]);
  const [selectedClarification, setSelectedClarification] = useState(null);
  const [showClarificationForm, setShowClarificationForm] = useState(false);
  const [showClarificationView, setShowClarificationView] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [pdfLoadError, setPdfLoadError] = useState(false);

  useEffect(() => {
    fetchRegistration();
    fetchClarifications();

    // Check if there's a clarification ID in URL
    const clarificationId = searchParams.get('clarification');
    if (clarificationId) {
      openClarification(clarificationId);
    }
  }, [searchParams]);

  const extractDocuments = (reg) => {
    // For company registrations, extract documents from compliance_info
    if (reg.compliance_info) {
      const complianceInfo = reg.compliance_info;
      const documentFields = [
        { field: 'financial_statement', label: 'Financial Statement' },
        { field: 'registration_certificate', label: 'Registration Certificate' },
        { field: 'iata_fiata_iso', label: 'IATA/FIATA/ISO Certificate' },
        { field: 'business_license', label: 'Business License' },
        { field: 'dg_certification', label: 'DG Certification' },
        { field: 'insurance_certificate', label: 'Insurance Certificate' }
      ];

      const documents = [];
      documentFields.forEach(({ field, label }) => {
        if (complianceInfo[field]) {
          let url = typeof complianceInfo[field] === 'object'
            ? complianceInfo[field].file_url || complianceInfo[field].url
            : complianceInfo[field];
          // Convert relative URLs to absolute URLs
          if (url && url.startsWith('/') && !url.startsWith(API_URL)) {
            url = `${API_URL}${url}`;
          }
          if (url) {
            documents.push({
              name: label,
              url: url,
              type: 'application/pdf'
            });
          }
        }
      });

      // Also check company_info for logo and brochure
      if (reg.company_info) {
        if (reg.company_info.company_logo) {
          let logoUrl = typeof reg.company_info.company_logo === 'object'
            ? reg.company_info.company_logo.file_url || reg.company_info.company_logo.url
            : reg.company_info.company_logo;
          // Convert relative URLs to absolute URLs
          if (logoUrl && logoUrl.startsWith('/') && !logoUrl.startsWith(API_URL)) {
            logoUrl = `${API_URL}${logoUrl}`;
          }
          if (logoUrl) {
            documents.push({
              name: 'Company Logo',
              url: logoUrl,
              type: 'image'
            });
          }
        }
        if (reg.company_info.company_brochure) {
          let brochureUrl = typeof reg.company_info.company_brochure === 'object'
            ? reg.company_info.company_brochure.file_url || reg.company_info.company_brochure.url
            : reg.company_info.company_brochure;
          // Convert relative URLs to absolute URLs
          if (brochureUrl && brochureUrl.startsWith('/') && !brochureUrl.startsWith(API_URL)) {
            brochureUrl = `${API_URL}${brochureUrl}`;
          }
          if (brochureUrl) {
            documents.push({
              name: 'Company Brochure',
              url: brochureUrl,
              type: 'application/pdf'
            });
          }
        }
      }

      return documents.length > 0 ? documents : null;
    }

    return null;
  };

  const fetchRegistration = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please login to view registration details');
        navigate('/login');
        return;
      }

      const response = await axios.get(`${API_URL}/api/company-registration/my-registration`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.data.exists) {
        toast.error('No registration found');
        navigate('/dashboard');
        return;
      }

      const reg = response.data;
      // Extract documents
      const documents = extractDocuments(reg);
      if (documents) {
        reg.documents = documents;
      }

      setRegistration(reg);
    } catch (error) {
      console.error('Failed to fetch registration:', error);
      toast.error('Failed to load registration details');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchClarifications = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get(`${API_URL}/api/clarifications/my-clarifications`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setClarifications(response.data.clarifications || []);
    } catch (error) {
      console.error('Failed to fetch clarifications:', error);
    }
  };

  const openClarification = async (clarificationId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please login to view clarification');
        return;
      }

      const response = await axios.get(`${API_URL}/api/clarifications/${clarificationId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const clarification = response.data;
      setSelectedClarification(clarification);

      // If status is "submitted", show view modal; otherwise show form
      if (clarification.status === 'submitted') {
        setShowClarificationView(true);
      } else {
        setShowClarificationForm(true);
      }
    } catch (error) {
      console.error('Failed to fetch clarification:', error);
      toast.error('Failed to load clarification details');
    }
  };

  const handleClarificationSubmitted = () => {
    setShowClarificationForm(false);
    setSelectedClarification(null);
    fetchClarifications();
    fetchRegistration(); // Refresh registration data
    toast.success('Clarification submitted successfully');
  };

  // Helper function to render read-only field
  const renderReadOnlyField = (label, value, isRequired = false) => {
    const displayValue = value || (value === 0 ? '0' : value === false ? 'No' : value === true ? 'Yes' : 'Not provided');
    return (
      <div className="p-4 border rounded-lg bg-gray-50">
        <p className="text-xs text-gray-600 mb-1 font-medium">{label}{isRequired ? ' *' : ''}</p>
        <p className="text-sm text-gray-900 break-words">{displayValue}</p>
      </div>
    );
  };

  // Render Company Information Section
  const renderCompanyInformationSection = () => {
    const companyInfo = registration.company_info || {};
    return (
      <div>
        <h3 className="text-lg font-medium text-gray-800 mb-4">Company Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {renderReadOnlyField('Company Name', companyInfo.company_name, true)}
          {renderReadOnlyField('Business Registration / License Number', companyInfo.business_registration_number)}
          {renderReadOnlyField('Trading Name', companyInfo.trading_name)}
          {renderReadOnlyField('Year of Establishment', companyInfo.year_established)}
          {renderReadOnlyField('Country of Registration', companyInfo.country_of_registration, true)}
          {renderReadOnlyField('Company Website', companyInfo.company_website)}
          {renderReadOnlyField('LinkedIn Page', companyInfo.linkedin_page)}
        </div>

        {/* Head Office Address */}
        <div className="mt-4">
          <h4 className="text-md font-medium text-gray-700 mb-3">Head Office Address</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderReadOnlyField('Street Address', companyInfo.street_address, true)}
            {renderReadOnlyField('Address Line 2', companyInfo.address_line_2)}
            {renderReadOnlyField('City', companyInfo.city)}
            {renderReadOnlyField('State/Region', companyInfo.state_region)}
            {renderReadOnlyField('Country', companyInfo.country, true)}
            {renderReadOnlyField('Postal Code', companyInfo.postal_code)}
          </div>
        </div>

        {/* Primary Contact */}
        <div className="mt-4">
          <h4 className="text-md font-medium text-gray-700 mb-3">Primary Contact</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderReadOnlyField('Contact Name', companyInfo.primary_contact_name, true)}
            {renderReadOnlyField('Designation', companyInfo.primary_contact_designation)}
            {renderReadOnlyField('Email', companyInfo.primary_contact_email, true)}
            {renderReadOnlyField('Phone', `${companyInfo.primary_contact_country_code || ''} ${companyInfo.primary_contact_mobile || ''}`.trim(), true)}
          </div>
        </div>

        {/* Secondary Contact */}
        {(companyInfo.secondary_contact_name || companyInfo.secondary_contact_email) && (
          <div className="mt-4">
            <h4 className="text-md font-medium text-gray-700 mb-3">Secondary Contact</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {renderReadOnlyField('Contact Name', companyInfo.secondary_contact_name)}
              {renderReadOnlyField('Designation', companyInfo.secondary_contact_designation)}
              {renderReadOnlyField('Email', companyInfo.secondary_contact_email)}
              {renderReadOnlyField('Phone', `${companyInfo.secondary_contact_country_code || ''} ${companyInfo.secondary_contact_mobile || ''}`.trim())}
            </div>
          </div>
        )}

        {/* Company Profile */}
        <div className="mt-4">
          <h4 className="text-md font-medium text-gray-700 mb-3">Company Profile</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderReadOnlyField('Total Number of Employees', companyInfo.total_employees, true)}
            {renderReadOnlyField('Languages Spoken', companyInfo.languages_spoken, true)}
            {renderReadOnlyField('Operating Days & Hours', companyInfo.operating_hours)}
            {renderReadOnlyField('Key Airports / Ports Covered', companyInfo.key_ports_airports, true)}
            {renderReadOnlyField('Service Capabilities', companyInfo.service_capabilities, true)}
            {renderReadOnlyField('Main Trade Lanes Served', companyInfo.main_trade_lanes, true)}
            {renderReadOnlyField('Markets to Develop Through Network', companyInfo.markets_to_develop, true)}
          </div>
          <div className="mt-4">
            {renderReadOnlyField('Company Strengths / Specialization', companyInfo.company_strengths, true)}
          </div>
        </div>
      </div>
    );
  };

  // Render Branch Offices Section
  const renderBranchOfficesSection = () => {
    const branches = registration.branches || [];
    if (branches.length === 0) return null;

    return (
      <div>
        <h3 className="text-lg font-medium text-gray-800 mb-4">Branch Offices</h3>
        <div className="space-y-4">
          {branches.map((branch, idx) => (
            <div key={idx} className="p-4 border rounded-lg bg-gray-50">
              <h4 className="font-medium text-gray-700 mb-3">Branch {idx + 1}</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {renderReadOnlyField('Branch Name', branch.branch_name)}
                {renderReadOnlyField('Address', branch.address || branch.street_address)}
                {renderReadOnlyField('City', branch.city)}
                {renderReadOnlyField('Country', branch.country)}
                {renderReadOnlyField('Contact Name', branch.contact_name)}
                {renderReadOnlyField('Contact Email', branch.contact_email)}
                {renderReadOnlyField('Contact Phone', `${branch.contact_country_code || branch.country_code || ''} ${branch.contact_mobile || branch.mobile || ''}`.trim())}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render Financial Information Section
  const renderFinancialInformationSection = () => {
    const financialInfo = registration.financial_info || {};
    const bankAccountNumber = financialInfo.account_number || financialInfo.bank_account_number;
    const tradeReferences = registration.trade_references || [];

    return (
      <div>
        <h3 className="text-lg font-medium text-gray-800 mb-4">Financial Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {renderReadOnlyField('Years in Operation', financialInfo.years_in_operation)}
          {renderReadOnlyField('Last Fiscal Turnover', financialInfo.last_fiscal_turnover ? `${financialInfo.turnover_currency || 'USD'} ${financialInfo.last_fiscal_turnover}` : null)}
          {renderReadOnlyField('Existing Memberships', financialInfo.existing_memberships)}
          {renderReadOnlyField('History of Disputes', financialInfo.history_of_disputes)}
          {financialInfo.history_of_disputes === 'yes' && renderReadOnlyField('Dispute Reasons', financialInfo.dispute_reasons)}
        </div>

        {/* Bank Information */}
        {(financialInfo.bank_name || financialInfo.swift_iban || bankAccountNumber) && (
          <div className="mt-4">
            <h4 className="text-md font-medium text-gray-700 mb-3">Bank Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {renderReadOnlyField('Bank Name', financialInfo.bank_name)}
              {renderReadOnlyField('Bank Branch Address', financialInfo.bank_branch_address)}
              {renderReadOnlyField('Bank Account Number', bankAccountNumber)}
              {renderReadOnlyField('SWIFT/IBAN', financialInfo.swift_iban)}
              {renderReadOnlyField('Account Holder Name', financialInfo.account_holder_name)}
            </div>
          </div>
        )}

        {/* Trade References */}
        {tradeReferences.length > 0 && (
          <div className="mt-4">
            <h4 className="text-md font-medium text-gray-700 mb-3">Trade References</h4>
            <div className="space-y-4">
              {tradeReferences.map((ref, idx) => (
                <div key={idx} className="p-4 border rounded-lg bg-gray-50">
                  <h5 className="font-medium text-gray-600 mb-3">Reference {idx + 1}</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {renderReadOnlyField('Company Name', ref.company_name)}
                    {renderReadOnlyField('Contact Person', ref.contact_person)}
                    {renderReadOnlyField('Email', ref.email)}
                    {renderReadOnlyField('Mobile', `${ref.country_code || ''} ${ref.mobile || ''}`.trim())}
                    {renderReadOnlyField('Relationship Duration', ref.relationship_duration)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render Compliance Section
  const renderComplianceSection = () => {
    const complianceInfo = registration.compliance_info || {};

    return (
      <div>
        <h3 className="text-lg font-medium text-gray-800 mb-4">Compliance</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {renderReadOnlyField('LEI Number', complianceInfo.lei_number)}
          {renderReadOnlyField('Tax Registration Number', complianceInfo.tax_registration_number)}
        </div>

        {/* Declarations */}
        <div className="mt-4">
          <h4 className="text-md font-medium text-gray-700 mb-3">Declarations</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderReadOnlyField('Information Accuracy Confirmed', complianceInfo.info_accuracy_confirmed)}
            {renderReadOnlyField('No Fraud Confirmed', complianceInfo.no_fraud_confirmed)}
            {renderReadOnlyField('Approval Process Acknowledged', complianceInfo.approval_process_acknowledged)}
            {renderReadOnlyField('Network Rules Agreed', complianceInfo.network_rules_agreed)}
          </div>
        </div>

        {/* Applicant Information */}
        <div className="mt-4">
          <h4 className="text-md font-medium text-gray-700 mb-3">Applicant Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderReadOnlyField('Applicant Name', complianceInfo.applicant_name)}
            {renderReadOnlyField('Applicant Designation', complianceInfo.applicant_designation)}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-500 mx-auto" />
          <p className="mt-4 text-gray-500">Loading registration details...</p>
        </div>
      </div>
    );
  }

  if (!registration) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Registration not found</p>
        <Button onClick={() => navigate('/dashboard')} className="mt-4">
          <ArrowLeft size={16} className="mr-2" />
          Back to Dashboard
        </Button>
      </div>
    );
  }

  const getStatusBadge = (status) => {
    const normalizedStatus = status?.trim().toLowerCase();
    const formattedStatus = formatStatus(status);
    
    if (normalizedStatus === 'draft') {
      return <Badge className="bg-gray-100 text-gray-700">{formattedStatus}</Badge>;
    } else if (normalizedStatus === 'submitted') {
      return <Badge className="bg-blue-100 text-blue-700">{formattedStatus}</Badge>;
    } else if (normalizedStatus === 'approved') {
      return <Badge className="bg-green-100 text-green-700">{formattedStatus}</Badge>;
    } else if (normalizedStatus === 'rejected') {
      return <Badge className="bg-red-100 text-red-700">{formattedStatus}</Badge>;
    } else if (normalizedStatus === 'on_hold') {
      return <Badge className="bg-orange-100 text-orange-700">{formattedStatus}</Badge>;
    } else {
      return <Badge className="bg-yellow-100 text-yellow-700">{formattedStatus || 'Pending Review'}</Badge>;
    }
  };

  return (
    <div className="space-y-6" data-testid="user-registration-details">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Company Registration Details</h1>
          <p className="text-gray-500">View your submitted registration information</p>
        </div>
      </div>

      {/* Status Card */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Registration Status</p>
              <div className="mt-1">
                {getStatusBadge(registration.status)}
              </div>
            </div>
            <div className="text-right">
              {registration.submitted_at && (
                <>
                  <p className="text-sm text-gray-600">Submitted On</p>
                  <p className="text-sm font-medium text-gray-900">
                    {new Date(registration.submitted_at).toLocaleDateString()}
                  </p>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Documents Section */}
      {registration.documents && registration.documents.length > 0 && (
        <Card>
          <CardContent className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Submitted Documents</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {registration.documents.map((doc, idx) => (
                <div key={idx} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    {doc.type?.includes('image') ? (
                      <Image size={24} className="text-blue-500 flex-shrink-0" />
                    ) : (
                      <FileText size={24} className="text-gray-500 flex-shrink-0" />
                    )}
                    <span className="text-sm font-medium truncate">{doc.name}</span>
                  </div>
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setSelectedDocument(doc);
                      setPdfLoadError(false);
                      setShowDocumentModal(true);
                    }}
                  >
                    <Eye size={16} />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Clarifications Section */}
      {clarifications.length > 0 && (
        <Card>
          <CardContent className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Clarifications</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {clarifications.map((clarification) => (
                    <tr key={clarification.clarification_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-gray-900">{clarification.clarification_id}</p>
                        <p className="text-xs text-gray-500">{clarification.invalid_fields?.length || 0} field(s) need clarification</p>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {clarification.requested_at ? new Date(clarification.requested_at).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-4 py-3">
                        {clarification.status === 'pending' && (
                          <Badge className="bg-yellow-100 text-yellow-700">Pending</Badge>
                        )}
                        {clarification.status === 'submitted' && (
                          <Badge className="bg-blue-100 text-blue-700">Submitted</Badge>
                        )}
                        {clarification.status === 'reviewed' && (
                          <Badge className="bg-green-100 text-green-700">Reviewed</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openClarification(clarification.clarification_id)}
                        >
                          <Eye size={14} className="mr-1" />
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Registration Fields Section */}
      <Card>
        <CardContent className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Registration Information</h2>

          <div className="space-y-6">
            {/* Company Information */}
            {renderCompanyInformationSection()}

            {/* Branch Offices */}
            {renderBranchOfficesSection()}

            {/* Financial Information */}
            {renderFinancialInformationSection()}

            {/* Compliance */}
            {renderComplianceSection()}
          </div>
        </CardContent>
      </Card>

      {/* Document Viewer Modal */}
      {showDocumentModal && selectedDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-6xl max-h-[90vh] w-full flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">{selectedDocument.name}</h3>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => window.open(selectedDocument.url, '_blank')}
                >
                  <ExternalLink size={16} className="mr-2" />
                  Open in New Tab
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowDocumentModal(false);
                    setSelectedDocument(null);
                    setPdfLoadError(false);
                  }}
                >
                  <X size={20} />
                </Button>
              </div>
            </div>
            
            {/* Document Content */}
            <div className="flex-1 overflow-auto p-4 flex items-center justify-center bg-gray-100">
              {selectedDocument.type?.includes('image') || selectedDocument.type === 'image' ? (
                <img
                  src={selectedDocument.url}
                  alt={selectedDocument.name}
                  className="max-w-full max-h-[calc(90vh-120px)] object-contain"
                  onError={() => {
                    setPdfLoadError(true);
                  }}
                />
              ) : selectedDocument.url?.toLowerCase().endsWith('.pdf') || 
                  selectedDocument.type?.includes('pdf') || 
                  selectedDocument.type === 'application/pdf' ? (
                <div className="w-full h-full flex flex-col">
                  {pdfLoadError ? (
                    <div className="flex flex-col items-center justify-center p-8 flex-1">
                      <FileText size={64} className="text-gray-400 mb-4" />
                      <p className="text-gray-600 mb-4">Unable to display PDF preview</p>
                      <Button
                        variant="outline"
                        onClick={() => window.open(selectedDocument.url, '_blank')}
                      >
                        <ExternalLink size={16} className="mr-2" />
                        Open in New Tab
                      </Button>
                    </div>
                  ) : (
                    <div className="w-full h-full">
                      <iframe
                        src={selectedDocument.url}
                        title={selectedDocument.name}
                        className="w-full h-[calc(90vh-120px)] border-0"
                        style={{ minHeight: '600px' }}
                        type="application/pdf"
                        onLoad={() => {
                          console.log('PDF iframe loaded:', selectedDocument.url);
                        }}
                        onError={(e) => {
                          console.error('PDF iframe error:', e);
                          setPdfLoadError(true);
                        }}
                      />
                      {/* Fallback: If iframe fails, show error message */}
                      {pdfLoadError && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center p-8 bg-white">
                          <FileText size={64} className="text-gray-400 mb-4" />
                          <p className="text-gray-600 mb-4">Unable to display PDF preview</p>
                          <p className="text-xs text-gray-500 mb-4">URL: {selectedDocument.url}</p>
                          <Button
                            variant="outline"
                            onClick={() => window.open(selectedDocument.url, '_blank')}
                          >
                            <ExternalLink size={16} className="mr-2" />
                            Open in New Tab
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center p-8">
                  <FileText size={64} className="text-gray-400 mb-4" />
                  <p className="text-gray-600 mb-4">No preview available</p>
                  <Button
                    variant="outline"
                    onClick={() => window.open(selectedDocument.url, '_blank')}
                  >
                    <ExternalLink size={16} className="mr-2" />
                    Open in New Tab
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Clarification Form Modal */}
      {showClarificationForm && selectedClarification && (
        <ClarificationForm
          clarification={selectedClarification}
          registration={registration}
          onClose={() => {
            setShowClarificationForm(false);
            setSelectedClarification(null);
          }}
          onSubmitted={handleClarificationSubmitted}
        />
      )}

      {/* Clarification View Modal (for submitted clarifications) */}
      {showClarificationView && selectedClarification && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Clarification Details</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowClarificationView(false);
                    setSelectedClarification(null);
                  }}
                >
                  <X size={20} />
                </Button>
              </div>

              {/* Clarification Info */}
              <div className="space-y-4 mb-6">
                <div>
                  <Label className="text-sm font-medium text-gray-700">Clarification ID</Label>
                  <p className="text-sm text-gray-900 mt-1">{selectedClarification.clarification_id}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700">Requested At</Label>
                  <p className="text-sm text-gray-900 mt-1">
                    {selectedClarification.requested_at
                      ? new Date(selectedClarification.requested_at).toLocaleString()
                      : 'N/A'}
                  </p>
                </div>
                {selectedClarification.validation_notes && (
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Validation Notes</Label>
                    <p className="text-sm text-gray-900 mt-1">{selectedClarification.validation_notes}</p>
                  </div>
                )}
              </div>

              {/* Fields with Admin Reason and User Response */}
              <div>
                <Label className="text-sm font-medium text-gray-700 mb-3 block">Fields Requiring Clarification</Label>
                <div className="space-y-4">
                  {selectedClarification.invalid_fields?.map((field, idx) => {
                    const fieldKey = field.field;
                    const userResponse = selectedClarification.response?.field_responses?.[fieldKey];
                    const getFieldLabel = (key) => {
                      return key
                        .replace(/_/g, ' ')
                        .replace(/\b\w/g, l => l.toUpperCase())
                        .replace(/company info\./gi, '')
                        .replace(/financial info\./gi, '')
                        .replace(/compliance info\./gi, '')
                        .replace(/branch \d+ /gi, 'Branch ');
                    };

                    return (
                      <div key={idx} className="p-4 border rounded-lg">
                        <div className="mb-3">
                          <p className="font-medium text-sm text-gray-900">{getFieldLabel(fieldKey)}</p>
                          <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                            <p className="text-xs font-medium text-red-700 mb-1">Admin Reason:</p>
                            <p className="text-xs text-red-600">{field.reason}</p>
                          </div>
                        </div>

                        {userResponse !== undefined && userResponse !== null && userResponse !== '' ? (
                          <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded">
                            <p className="text-xs font-medium text-green-700 mb-1">User Response:</p>
                            <p className="text-sm text-gray-900">{userResponse}</p>
                          </div>
                        ) : (
                          <div className="mt-3 p-2 bg-gray-50 border border-gray-200 rounded">
                            <p className="text-xs text-gray-500">No response yet</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {selectedClarification.response?.user_description && (
                <div className="mt-4 p-3 border rounded-lg">
                  <Label className="text-sm font-medium text-gray-700">User Description</Label>
                  <p className="text-sm text-gray-900 mt-1">{selectedClarification.response.user_description}</p>
                </div>
              )}

              {selectedClarification.response && (
                <div className="mt-4">
                  <Label className="text-sm font-medium text-gray-700">Response Submitted At</Label>
                  <p className="text-sm text-gray-900 mt-1">
                    {selectedClarification.response_submitted_at
                      ? new Date(selectedClarification.response_submitted_at).toLocaleString()
                      : 'N/A'}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default UserRegistrationDetails;
